/**
 * InfoPdfPage – Typ A: PDF-Verwaltung für ein Info-Modul (wie Planogramme, aber generisch).
 * Unterstützt Bereiche, Thumbnails, Benachrichtigungen, Grid/Listen-Ansicht, Mobile-First.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Plus, Search, LayoutGrid, List, Trash2, Pencil, Eye,
  Upload, X, Loader2, FolderOpen, Tag, Download, Printer,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// ──────────────────────────────────────────────────────────────────────────────
// Upload Modal
// ──────────────────────────────────────────────────────────────────────────────
function InfoPdfUploadModal({ moduleId, moduleName, bereiche, locations, onSuccess, onClose }) {
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
  const [form, setForm] = useState({
    regal_name: '', titel: '', bereich_id: '', station_ids: [], gueltig_ab: '',
  });
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef();

  const allLocations = locations || [];

  const handleFile = (f) => {
    if (!f?.name?.toLowerCase().endsWith('.pdf')) { toast.error('Nur PDF-Dateien'); return; }
    setFile(f);
    if (!form.regal_name) setForm(p => ({ ...p, regal_name: f.name.replace('.pdf', '') }));
  };

  const toggleStation = (id) =>
    setForm(p => ({
      ...p,
      station_ids: p.station_ids.includes(id)
        ? p.station_ids.filter(s => s !== id)
        : [...p.station_ids, id],
    }));

  const handleSubmit = async () => {
    if (!form.regal_name.trim()) { toast.error('Regalname ist Pflicht'); return; }
    if (!form.titel.trim()) { toast.error('Titel ist Pflicht'); return; }
    if (!file) { toast.error('Bitte PDF auswählen'); return; }
    if (form.station_ids.length === 0) { toast.error('Mindestens eine Station wählen'); return; }

    setUploading(true);
    const fd = new FormData();
    fd.append('regal_name', form.regal_name);
    fd.append('titel', form.titel);
    fd.append('bereich_id', form.bereich_id || '');
    fd.append('station_ids', JSON.stringify(form.station_ids));
    fd.append('gueltig_ab', form.gueltig_ab || '');
    fd.append('pdf_file', file);
    try {
      await axios.post(`${API}/api/info/${moduleId}/upload`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      toast.success('PDF hochgeladen');
      onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload fehlgeschlagen');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>PDF hochladen – {moduleName}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {/* Drag & Drop Zone */}
          <div
            className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${dragOver ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`}
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={e => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files[0]); }}
            onClick={() => fileRef.current?.click()}
          >
            <input ref={fileRef} type="file" accept=".pdf" className="hidden"
              onChange={e => handleFile(e.target.files[0])} />
            {file ? (
              <div className="flex items-center justify-center gap-2 text-sm font-medium text-primary">
                <Upload className="h-4 w-4" />
                {file.name}
                <button type="button" className="text-destructive" onClick={e => { e.stopPropagation(); setFile(null); }}>
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <>
                <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">PDF hierher ziehen oder klicken</p>
              </>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-sm font-medium">Regalname *</label>
              <Input value={form.regal_name} onChange={e => setForm(p => ({ ...p, regal_name: e.target.value }))}
                placeholder="z.B. Regal A3" />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Titel *</label>
              <Input value={form.titel} onChange={e => setForm(p => ({ ...p, titel: e.target.value }))}
                placeholder="z.B. Getränke Sommer" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {bereiche.length > 0 && (
              <div className="space-y-1">
                <label className="text-sm font-medium">Bereich</label>
                <Select value={form.bereich_id} onValueChange={v => setForm(p => ({ ...p, bereich_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Kein Bereich" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Kein Bereich</SelectItem>
                    {bereiche.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1">
              <label className="text-sm font-medium">Gültig ab</label>
              <Input type="date" value={form.gueltig_ab} onChange={e => setForm(p => ({ ...p, gueltig_ab: e.target.value }))} />
            </div>
          </div>

          {/* Stationen */}
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">Standorte *</label>
              <button type="button" className="text-xs text-primary underline"
                onClick={() => setForm(p => ({ ...p, station_ids: p.station_ids.length === allLocations.length ? [] : allLocations.map(l => l.id) }))}>
                {form.station_ids.length === allLocations.length ? 'Keine' : 'Alle'}
              </button>
            </div>
            <div className="grid grid-cols-2 gap-1.5 max-h-36 overflow-y-auto border rounded-md p-2">
              {allLocations.map(loc => (
                <label key={loc.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted rounded px-1 py-0.5">
                  <input type="checkbox" checked={form.station_ids.includes(loc.id)} onChange={() => toggleStation(loc.id)} />
                  {loc.name}
                </label>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button onClick={handleSubmit} disabled={uploading}>
            {uploading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Hochladen…</> : 'Hochladen'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// PDF Karte
// ──────────────────────────────────────────────────────────────────────────────
function InfoPdfCard({ file, moduleId, bereiche, locations, onDeleted, onEdited, canWrite, viewMode }) {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const bereichName = bereiche.find(b => b.id === file.bereich_id)?.name;
  const locNames = (file.station_ids || [])
    .map(id => locations.find(l => l.id === id)?.name || id)
    .join(', ');

  const handleDelete = async () => {
    if (!window.confirm('PDF löschen?')) return;
    try {
      await axios.delete(`${API}/api/info/${moduleId}/files/${file.id}`, { headers });
      toast.success('Gelöscht');
      onDeleted();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };

  const pdfUrl = `${API}/api/info/${moduleId}/files/${file.id}/pdf?token=${token}`;
  const thumbUrl = `${API}/api/info/${moduleId}/files/${file.id}/thumbnail?token=${token}`;

  const handleOpenPdf = () => window.open(pdfUrl, '_blank', 'noopener,noreferrer');

  const handleDownloadPdf = async () => {
    try {
      const res = await fetch(pdfUrl);
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${file.regal_name || file.titel || 'dokument'}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error('Download fehlgeschlagen'); }
  };

  const handlePrintPdf = () => {
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = pdfUrl;
    document.body.appendChild(iframe);
    iframe.onload = () => { iframe.contentWindow.print(); };
    setTimeout(() => document.body.removeChild(iframe), 30000);
  };

  if (viewMode === 'list') {
    return (
      <div className="flex items-center gap-3 p-3 border rounded-lg hover:bg-muted/30 transition-colors">
        <button
          onClick={handleOpenPdf}
          className="flex-shrink-0 w-10 h-12 bg-red-50 rounded flex items-center justify-center border hover:bg-red-100 transition-colors"
          title="PDF öffnen"
        >
          <span className="text-xs font-bold text-red-600">PDF</span>
        </button>
        <div className="flex-1 min-w-0 cursor-pointer" onClick={handleOpenPdf}>
          <div className="font-medium text-sm truncate">{file.regal_name}</div>
          <div className="text-xs text-muted-foreground truncate">{file.titel}</div>
          <div className="text-xs text-muted-foreground">{locNames}</div>
        </div>
        {bereichName && <Badge variant="outline" className="text-xs shrink-0">{bereichName}</Badge>}
        <div className="flex items-center gap-1 shrink-0">
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleDownloadPdf} title="Herunterladen">
            <Download className="h-3.5 w-3.5" />
          </Button>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handlePrintPdf} title="Drucken">
            <Printer className="h-3.5 w-3.5" />
          </Button>
          {canWrite && (
            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={handleDelete}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <Card className="overflow-hidden hover:shadow-md transition-shadow group">
      <button onClick={handleOpenPdf} className="block w-full text-left">
        <div className="relative bg-gray-100 aspect-[3/4]">
          <img
            src={thumbUrl}
            alt={file.titel}
            className="w-full h-full object-cover"
            onError={e => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <div className="hidden w-full h-full items-center justify-center bg-red-50">
            <span className="text-2xl font-bold text-red-400">PDF</span>
          </div>
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
            <Eye className="h-8 w-8 text-white" />
          </div>
        </div>
      </button>
      <CardContent className="p-3 space-y-1">
        <div className="font-medium text-sm line-clamp-1">{file.regal_name}</div>
        <div className="text-xs text-muted-foreground line-clamp-1">{file.titel}</div>
        <div className="flex items-center justify-between gap-1 pt-1">
          {bereichName
            ? <Badge variant="outline" className="text-xs">{bereichName}</Badge>
            : <span />}
          <div className="flex items-center gap-0.5">
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleDownloadPdf} title="Herunterladen">
              <Download className="h-3.5 w-3.5" />
            </Button>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handlePrintPdf} title="Drucken">
              <Printer className="h-3.5 w-3.5" />
            </Button>
            {canWrite && (
              <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive"
                onClick={handleDelete}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Hauptseite
// ──────────────────────────────────────────────────────────────────────────────
export default function InfoPdfPage() {
  const { moduleId } = useParams();
  const { user } = useAuth();
  const [module, setModule] = useState(null);
  const [files, setFiles] = useState([]);
  const [bereiche, setBereiche] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBereich, setFilterBereich] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [showUpload, setShowUpload] = useState(false);
  const [showBereichDialog, setShowBereichDialog] = useState(false);
  const [newBereichName, setNewBereichName] = useState('');
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
  const isAdmin = ['admin', 'superadmin', 'stationsleitung'].includes(user?.role);
  const canWrite = isAdmin;

  const fetchAll = useCallback(async () => {
    try {
      const [mRes, fRes, bRes, lRes] = await Promise.all([
        axios.get(`${API}/api/info/modules`, { headers }),
        axios.get(`${API}/api/info/${moduleId}/files`, { headers }),
        axios.get(`${API}/api/info/${moduleId}/bereiche`, { headers }),
        axios.get(`${API}/api/user/locations`, { headers }),
      ]);
      const found = (mRes.data || []).find(m => m.id === moduleId);
      setModule(found || null);
      setFiles(fRes.data || []);
      setBereiche(bRes.data || []);
      setLocations(lRes.data || []);
    } catch (e) {
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  }, [moduleId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleCreateBereich = async () => {
    if (!newBereichName.trim()) return;
    try {
      await axios.post(`${API}/api/info/${moduleId}/bereiche`, { name: newBereichName.trim() }, { headers });
      toast.success('Bereich erstellt');
      setNewBereichName('');
      setShowBereichDialog(false);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };

  const handleDeleteBereich = async (id, name) => {
    if (!window.confirm(`Bereich "${name}" löschen?`)) return;
    try {
      await axios.delete(`${API}/api/info/${moduleId}/bereiche/${id}`, { headers });
      toast.success('Bereich gelöscht');
      if (filterBereich === id) setFilterBereich('all');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };

  const filtered = files.filter(f => {
    const matchSearch = !searchTerm ||
      f.regal_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      f.titel?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchBereich = filterBereich === 'all' ||
      (filterBereich === 'none' ? !f.bereich_id : f.bereich_id === filterBereich);
    return matchSearch && matchBereich;
  });

  if (loading) return (
    <DashboardLayout>
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    </DashboardLayout>
  );

  if (!module) return (
    <DashboardLayout>
      <div className="p-6 text-center text-muted-foreground">Modul nicht gefunden.</div>
    </DashboardLayout>
  );

  return (
    <DashboardLayout>
      <div className="space-y-5 p-4 md:p-6" data-testid={`info-pdf-page-${moduleId}`}>
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{module.name}</h1>
            <p className="text-sm text-muted-foreground mt-1">
              {files.length} Dokument{files.length !== 1 ? 'e' : ''} gespeichert
            </p>
          </div>
          {canWrite && (
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" size="sm" onClick={() => setShowBereichDialog(true)}>
                <Tag className="h-4 w-4 mr-1" /> Bereich
              </Button>
              <Button size="sm" onClick={() => setShowUpload(true)}
                data-testid={`upload-info-pdf-btn-${moduleId}`}>
                <Upload className="h-4 w-4 mr-1" /> PDF hochladen
              </Button>
            </div>
          )}
        </div>

        {/* Filter-Leiste */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={`In ${module.name} suchen...`}
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={filterBereich} onValueChange={setFilterBereich}>
            <SelectTrigger className="w-full sm:w-48">
              <SelectValue placeholder="Alle Bereiche" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Bereiche</SelectItem>
              <SelectItem value="none">Ohne Bereich</SelectItem>
              {bereiche.map(b => (
                <SelectItem key={b.id} value={b.id}>
                  <div className="flex items-center justify-between gap-4 w-full">
                    {b.name}
                    {canWrite && (
                      <button type="button" className="text-destructive hover:opacity-70 ml-2"
                        onClick={e => { e.stopPropagation(); handleDeleteBereich(b.id, b.name); }}>
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex gap-1 border rounded-md p-1 shrink-0">
            {['grid', 'list'].map(mode => (
              <Button key={mode} variant={viewMode === mode ? 'default' : 'ghost'}
                size="icon" className="h-8 w-8"
                onClick={() => setViewMode(mode)}>
                {mode === 'grid' ? <LayoutGrid className="h-4 w-4" /> : <List className="h-4 w-4" />}
              </Button>
            ))}
          </div>
        </div>

        {/* Content */}
        {filtered.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <FolderOpen className="h-10 w-10 mx-auto mb-3 text-muted-foreground/40" />
              <p className="text-sm text-muted-foreground">
                {searchTerm ? 'Keine Ergebnisse' : `Noch keine Dokumente in "${module.name}".`}
              </p>
              {canWrite && !searchTerm && (
                <Button className="mt-3" size="sm" onClick={() => setShowUpload(true)}>
                  <Upload className="h-4 w-4 mr-1" /> Erstes PDF hochladen
                </Button>
              )}
            </CardContent>
          </Card>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {filtered.map(f => (
              <InfoPdfCard key={f.id} file={f} moduleId={moduleId}
                bereiche={bereiche} locations={locations}
                canWrite={canWrite} viewMode="grid"
                onDeleted={fetchAll} onEdited={fetchAll} />
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map(f => (
              <InfoPdfCard key={f.id} file={f} moduleId={moduleId}
                bereiche={bereiche} locations={locations}
                canWrite={canWrite} viewMode="list"
                onDeleted={fetchAll} onEdited={fetchAll} />
            ))}
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {showUpload && (
        <InfoPdfUploadModal
          moduleId={moduleId}
          moduleName={module.name}
          bereiche={bereiche}
          locations={locations}
          onSuccess={() => { setShowUpload(false); fetchAll(); }}
          onClose={() => setShowUpload(false)}
        />
      )}

      {/* Bereich erstellen Dialog */}
      <Dialog open={showBereichDialog} onOpenChange={setShowBereichDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Bereich erstellen</DialogTitle></DialogHeader>
          <div className="py-2">
            <Input value={newBereichName} onChange={e => setNewBereichName(e.target.value)}
              placeholder="z.B. Getränke, Snacks, Kassenvorgänge..."
              onKeyDown={e => e.key === 'Enter' && handleCreateBereich()} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBereichDialog(false)}>Abbrechen</Button>
            <Button onClick={handleCreateBereich}>Erstellen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
